/**
 * features/library/hooks.ts — React Query hooks for the Library feature.
 *
 * All hooks key on tenantId + kind to keep position and unit caches separate.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import {
  createJobTemplate,
  createLibraryItem,
  customizeJobTemplate,
  customizeLibraryItem,
  deleteJobTemplate,
  deleteLibraryItem,
  getJobTemplate,
  getLibraryItem,
  linkAttachmentToJobTemplate,
  listJobTemplateAttachments,
  listJobTemplates,
  listLibrary,
  unlinkAttachmentFromJobTemplate,
  updateJobTemplate,
  updateLibraryItem,
} from './api';
import type {
  ItemFormValues,
  JobTemplateCreateBody,
  JobTemplateCustomizeResponse,
  JobTemplateDeleteResponse,
  JobTemplateDetailResponse,
  JobTemplateMutationResponse,
  JobTemplatesListResponse,
  JobTemplateUpdateBody,
  LibraryCustomizeResponse,
  LibraryDeleteResponse,
  LibraryItemDetailResponse,
  LibraryItemMutationResponse,
  LibraryKind,
  LibraryListResponse,
} from './schemas';

function useActiveTenantId(): string {
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  if (!tenantId) throw new Error('useActiveTenantId called without an active tenant');
  return tenantId;
}

export function useLibraryList(kind: LibraryKind) {
  const tenantId = useActiveTenantId();
  return useQuery<LibraryListResponse>({
    queryKey: ['library', tenantId, kind, 'list'] as const,
    queryFn: () => listLibrary(tenantId, kind),
  });
}

export function useLibraryItem(kind: LibraryKind, id: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery<LibraryItemDetailResponse>({
    queryKey: ['library', tenantId, kind, 'item', id ?? '__none'] as const,
    queryFn: () => getLibraryItem(tenantId, kind, id!),
    enabled: Boolean(id),
  });
}

function invalidate(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantId: string,
  kind: LibraryKind,
) {
  return queryClient.invalidateQueries({ queryKey: ['library', tenantId, kind] });
}

export function useCreateLibraryItem(kind: LibraryKind) {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<LibraryItemMutationResponse, unknown, ItemFormValues>({
    mutationFn: (values) => createLibraryItem(tenantId, kind, values),
    onSuccess: async () => {
      await invalidate(queryClient, tenantId, kind);
    },
  });
}

export function useCustomizeLibraryItem(kind: LibraryKind) {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<LibraryCustomizeResponse, unknown, { systemRowId: string }>({
    mutationFn: ({ systemRowId }) => customizeLibraryItem(tenantId, kind, systemRowId),
    onSuccess: async () => {
      await invalidate(queryClient, tenantId, kind);
    },
  });
}

export function useUpdateLibraryItem(kind: LibraryKind) {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<
    LibraryItemMutationResponse,
    unknown,
    { id: string; patch: { name?: string; description?: string | null } }
  >({
    mutationFn: ({ id, patch }) => updateLibraryItem(tenantId, kind, id, patch),
    onSuccess: async () => {
      await invalidate(queryClient, tenantId, kind);
    },
  });
}

export function useDeleteLibraryItem(kind: LibraryKind) {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<LibraryDeleteResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => deleteLibraryItem(tenantId, kind, id),
    onSuccess: async () => {
      await invalidate(queryClient, tenantId, kind);
    },
  });
}

export function useJobTemplatesList() {
  const tenantId = useActiveTenantId();
  return useQuery<JobTemplatesListResponse>({
    queryKey: ['library', tenantId, 'job-template', 'list'] as const,
    queryFn: () => listJobTemplates(tenantId),
  });
}

export function useJobTemplateDetail(id: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery<JobTemplateDetailResponse>({
    queryKey: ['library', tenantId, 'job-template', 'detail', id ?? '__none'] as const,
    queryFn: () => getJobTemplate(tenantId, id!),
    enabled: Boolean(id),
  });
}

function invalidateJobTemplates(queryClient: ReturnType<typeof useQueryClient>, tenantId: string) {
  return queryClient.invalidateQueries({ queryKey: ['library', tenantId, 'job-template'] });
}

export function useCreateJobTemplate() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<JobTemplateMutationResponse, unknown, JobTemplateCreateBody>({
    mutationFn: (body) => createJobTemplate(tenantId, body),
    onSuccess: async () => {
      await invalidateJobTemplates(queryClient, tenantId);
    },
  });
}

export function useCustomizeJobTemplate() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<JobTemplateCustomizeResponse, unknown, { systemRowId: string }>({
    mutationFn: ({ systemRowId }) => customizeJobTemplate(tenantId, systemRowId),
    onSuccess: async () => {
      await invalidateJobTemplates(queryClient, tenantId);
    },
  });
}

export function useUpdateJobTemplate() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<
    JobTemplateMutationResponse,
    unknown,
    { id: string; patch: JobTemplateUpdateBody }
  >({
    mutationFn: ({ id, patch }) => updateJobTemplate(tenantId, id, patch),
    onSuccess: async () => {
      await invalidateJobTemplates(queryClient, tenantId);
    },
  });
}

export function useDeleteJobTemplate() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<JobTemplateDeleteResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => deleteJobTemplate(tenantId, id),
    onSuccess: async () => {
      await invalidateJobTemplates(queryClient, tenantId);
    },
  });
}

// ── Job Template Attachments ─────────────────────────────────────────────────

export function useJobTemplateAttachments(templateId: string | null) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['job-template-attachments', tenantId, templateId],
    queryFn: () => listJobTemplateAttachments(tenantId, templateId!),
    enabled: Boolean(templateId),
  });
}

export function useLinkJobTemplateAttachment(templateId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId }: { attachmentId: string }) =>
      linkAttachmentToJobTemplate(tenantId, templateId, attachmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['job-template-attachments', tenantId, templateId] });
    },
  });
}

export function useUnlinkJobTemplateAttachment(templateId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId }: { attachmentId: string }) =>
      unlinkAttachmentFromJobTemplate(tenantId, templateId, attachmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['job-template-attachments', tenantId, templateId] });
    },
  });
}
